import React from "react";
import {
    Button,
    ButtonContainer,
    ButtonType,
    DateInput,
    Dropdown,
    Form,
    Inline,
    Modal,
    ModalSize,
    TwoColumns,
} from "@renta-apps/athenaeum-react-components";
import User from "@/models/server/User";
import UserDefaultWorkOrderValues from "@/models/server/UserDefaultWorkOrderValues";
import SaveAbsenceRequest from "@/models/server/requests/SaveAbsenceRequest";
import SaveAbsenceResponse from "@/models/server/responses/SaveAbsenceResponse";
import GetUserAbsencesRequest from "@/models/server/requests/GetUserAbsencesRequest";
import UserAbsenceItem from "@/models/server/UserAbsenceItem";
import Localizer from "@/localization/Localizer";

import styles from "./AbsencesManagementTab.module.scss";

enum AbsenceStatus {
    Past,
    Ongoing,
    Upcoming,
}

interface AbsencesManagementTabProps {
    user: User | null;
    managers: User[];
    defaultWorkOrderValues: UserDefaultWorkOrderValues | null;
    onSave: (request: SaveAbsenceRequest) => Promise<SaveAbsenceResponse>;
    onLoadAbsences: (request: GetUserAbsencesRequest) => Promise<UserAbsenceItem[]>;
    onDelete: (workOrderId: string) => Promise<void>;
    isPerformingAction: boolean;
}

interface AbsencesManagementTabState {
    startDate: Date | null;
    endDate: Date | null;
    selectedManager: User | null;
    selectedWorkOrderId: string | null;
    absences: UserAbsenceItem[];
}

export default class AbsencesManagementTab extends React.Component<AbsencesManagementTabProps, AbsencesManagementTabState> {

    state: AbsencesManagementTabState = {
        startDate: null,
        endDate: null,
        selectedManager: null,
        selectedWorkOrderId: null,
        absences: [],
    };

    private readonly _modalRef: React.RefObject<Modal> = React.createRef();

    public async componentDidMount(): Promise<void> {
        await this.loadAbsencesAsync();
    }

    public async componentDidUpdate(prevProps: AbsencesManagementTabProps): Promise<void> {
        if (this.props.user?.id !== prevProps.user?.id) {
            await this.loadAbsencesAsync();
        }
    }

    private get defaultManager(): User | null {
        const defaultValues = this.props.defaultWorkOrderValues;
        return defaultValues?.managerId
            ? this.props.managers.find(m => m.id === defaultValues.managerId) ?? this.props.managers[0] ?? null
            : this.props.managers[0] ?? null;
    }

    private async loadAbsencesAsync(): Promise<void> {
        if (!this.props.user?.id) {
            await this.setState({ absences: [] });
            return;
        }

        const request = new GetUserAbsencesRequest();
        request.userId = this.props.user.id;
        const absences = await this.props.onLoadAbsences(request);
        await this.setState({ absences });
    }

    private async onStartDateChangeAsync(value: Date | null): Promise<void> {
        await this.setState({ startDate: value });
    }

    private async onEndDateChangeAsync(value: Date | null): Promise<void> {
        await this.setState({ endDate: value });
    }

    private async onManagerChangeAsync(dropdown: Dropdown<User>): Promise<void> {
        await this.setState({ selectedManager: dropdown.selectedItem ?? null });
    }

    private async openAddAsync(): Promise<void> {
        await this.setState({
            selectedWorkOrderId: null,
            startDate: null,
            endDate: null,
            selectedManager: this.defaultManager,
        });
        await this._modalRef.current?.openAsync();
    }

    private async openEditAsync(absence: UserAbsenceItem): Promise<void> {
        const manager = absence.managerId
            ? this.props.managers.find(m => m.id === absence.managerId) ?? null
            : null;

        await this.setState({
            selectedWorkOrderId: absence.workOrderId,
            startDate: new Date(absence.startDate),
            endDate: new Date(absence.endDate),
            selectedManager: manager,
        });
        await this._modalRef.current?.openAsync();
    }

    /** Clears the values chosen for the current (unsaved) absence, keeping the dialog open. */
    private async clearFormAsync(): Promise<void> {
        await this.setState({
            startDate: null,
            endDate: null,
            selectedManager: this.defaultManager,
        });
    }

    private async cancelAsync(): Promise<void> {
        await this._modalRef.current?.closeAsync();
    }

    private get isDateRangeValid(): boolean {
        const { startDate, endDate } = this.state;
        return !startDate || !endDate || endDate >= startDate;
    }

    private get canSave(): boolean {
        return !!this.props.user
            && !!this.state.startDate
            && !!this.state.endDate
            && !!this.state.selectedManager
            && this.isDateRangeValid
            && !this.props.isPerformingAction;
    }

    private async saveAsync(): Promise<void> {
        if (!this.canSave || !this.props.user) {
            return;
        }

        const request = new SaveAbsenceRequest();
        request.userId = this.props.user.id;
        request.workOrderId = this.state.selectedWorkOrderId;
        request.startDate = this.state.startDate!;
        request.endDate = this.state.endDate!;
        request.managerId = this.state.selectedManager!.id;

        // The parent page surfaces success/error via the standard alert; only react to the outcome here.
        const response = await this.props.onSave(request);
        if (!response.success) {
            return;
        }

        await this._modalRef.current?.closeAsync();
        await this.loadAbsencesAsync();
    }

    private async deleteAbsenceAsync(workOrderId: string): Promise<void> {
        if (this.props.isPerformingAction) {
            return;
        }

        await this.props.onDelete(workOrderId);
        if (this._modalRef.current?.isOpen) {
            await this._modalRef.current.closeAsync();
        }
        await this.loadAbsencesAsync();
    }

    private static toDateOnly(value: Date): Date {
        const date = new Date(value);
        date.setHours(0, 0, 0, 0);
        return date;
    }

    private statusOf(absence: UserAbsenceItem): AbsenceStatus {
        const today: Date = AbsencesManagementTab.toDateOnly(new Date());
        const start: Date = AbsencesManagementTab.toDateOnly(new Date(absence.startDate));
        const end: Date = AbsencesManagementTab.toDateOnly(new Date(absence.endDate));

        if (end < today) {
            return AbsenceStatus.Past;
        }
        if (start > today) {
            return AbsenceStatus.Upcoming;
        }
        return AbsenceStatus.Ongoing;
    }

    private statusLabel(status: AbsenceStatus): string {
        switch (status) {
            case AbsenceStatus.Ongoing: return Localizer.absenceStatusOngoing;
            case AbsenceStatus.Upcoming: return Localizer.absenceStatusUpcoming;
            default: return Localizer.absenceStatusPast;
        }
    }

    private statusClassName(status: AbsenceStatus): string {
        switch (status) {
            case AbsenceStatus.Ongoing: return styles.statusOngoing;
            case AbsenceStatus.Upcoming: return styles.statusUpcoming;
            default: return styles.statusPast;
        }
    }

    private durationLabel(absence: UserAbsenceItem): string {
        const start: Date = AbsencesManagementTab.toDateOnly(new Date(absence.startDate));
        const end: Date = AbsencesManagementTab.toDateOnly(new Date(absence.endDate));
        const days: number = Math.floor((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000)) + 1;
        return Localizer.get(Localizer.absenceDurationDaysShort, days);
    }

    private managerName(managerId: string | null | undefined): string {
        if (!managerId) {
            return "";
        }
        const manager = this.props.managers.find(m => m.id === managerId);
        return manager ? `${manager.firstname} ${manager.lastName}` : "";
    }

    private renderAbsenceRow(absence: UserAbsenceItem): React.ReactNode {
        const status: AbsenceStatus = this.statusOf(absence);
        const meta: string = [this.durationLabel(absence), this.managerName(absence.managerId)]
            .filter(part => !!part)
            .join(" · ");

        return (
            <div
                key={absence.workOrderId}
                id={`absenceListItem_${absence.workOrderId}`}
                className={styles.listItem}
            >
                <div className={styles.itemMain}>
                    <div className={styles.itemDates}>
                        {new Date(absence.startDate).toLocaleDateString()}
                        {" – "}
                        {new Date(absence.endDate).toLocaleDateString()}
                    </div>
                    <div className={styles.itemMeta}>{meta}</div>
                </div>

                <div className={styles.itemActions}>
                    <span className={`${styles.status} ${this.statusClassName(status)}`}>
                        {this.statusLabel(status)}
                    </span>

                    <Button
                        small
                        id={`absenceEdit_${absence.workOrderId}`}
                        type={ButtonType.Default}
                        icon={{ name: "fas pencil-alt", tooltip: Localizer.genericEdit }}
                        disabled={this.props.isPerformingAction}
                        onClick={() => this.openEditAsync(absence)}
                    />

                    <Button
                        small
                        id={`absenceDelete_${absence.workOrderId}`}
                        type={ButtonType.Danger}
                        icon={{ name: "fas trash-alt", tooltip: Localizer.genericDelete }}
                        disabled={this.props.isPerformingAction}
                        confirm={Localizer.absenceConfirmDelete}
                        onClick={() => this.deleteAbsenceAsync(absence.workOrderId)}
                    />
                </div>
            </div>
        );
    }

    public render(): React.ReactNode {
        if (!this.props.user) {
            return null;
        }

        const { isPerformingAction, managers } = this.props;
        const { absences, selectedWorkOrderId } = this.state;
        const isEditing: boolean = !!selectedWorkOrderId;

        return (
            <div className={styles.absencesTab}>
                <div className={styles.header}>
                    <h5>{Localizer.absenceSectionTitle}</h5>
                    <Button
                        id="absenceAdd"
                        type={ButtonType.Orange}
                        icon={{ name: "fas plus" }}
                        label={Localizer.absenceAdd}
                        disabled={isPerformingAction}
                        onClick={() => this.openAddAsync()}
                    />
                </div>

                {
                    absences.length === 0
                        ? (
                            <div className={styles.empty} id="absenceEmptyState">
                                {Localizer.absenceNoneRegistered}
                            </div>
                        )
                        : (
                            <div className={styles.list}>
                                {absences.map(absence => this.renderAbsenceRow(absence))}
                            </div>
                        )
                }

                <Modal
                    id="absenceModal"
                    ref={this._modalRef}
                    size={ModalSize.Default}
                    title={isEditing ? Localizer.absenceEditTitle : Localizer.absenceAdd}
                >
                    <Form className={styles.absenceForm}>
                        <TwoColumns>
                            <DateInput
                                id="absenceStartDate"
                                label={Localizer.absenceStartDate}
                                value={this.state.startDate}
                                readonly={isPerformingAction}
                                onChange={async (value) => await this.onStartDateChangeAsync(value)}
                            />

                            <DateInput
                                id="absenceEndDate"
                                label={Localizer.absenceEndDate}
                                value={this.state.endDate}
                                readonly={isPerformingAction}
                                onChange={async (value) => await this.onEndDateChangeAsync(value)}
                            />
                        </TwoColumns>

                        {
                            !this.isDateRangeValid &&
                            (
                                <Inline>
                                    <span id="absenceDateRangeError" className={styles.error}>
                                        {Localizer.absenceInvalidDateRange}
                                    </span>
                                </Inline>
                            )
                        }

                        <Dropdown<User>
                            id="absenceManager"
                            items={managers}
                            disabled={isPerformingAction}
                            label={Localizer.genericManager}
                            selectedItem={this.state.selectedManager ?? undefined}
                            onChange={async (dropdown) => await this.onManagerChangeAsync(dropdown)}
                        />

                        <ButtonContainer className={styles.modalButtons}>
                            {
                                isEditing &&
                                (
                                    <Button
                                        id="absenceDelete"
                                        type={ButtonType.Danger}
                                        icon={{ name: "fas trash-alt" }}
                                        disabled={isPerformingAction}
                                        confirm={Localizer.absenceConfirmDelete}
                                        onClick={() => this.deleteAbsenceAsync(selectedWorkOrderId!)}
                                        label={Localizer.genericDelete}
                                    />
                                )
                            }

                            <Button
                                right
                                id="absenceClear"
                                type={ButtonType.Default}
                                disabled={isPerformingAction}
                                onClick={() => this.clearFormAsync()}
                                label={Localizer.absenceClear}
                            />

                            <Button
                                right
                                id="absenceCancel"
                                type={ButtonType.Default}
                                disabled={isPerformingAction}
                                onClick={() => this.cancelAsync()}
                                label={Localizer.genericCancel}
                            />

                            <Button
                                right
                                id="absenceSave"
                                type={ButtonType.Primary}
                                disabled={!this.canSave}
                                onClick={() => this.saveAsync()}
                                label={Localizer.formSave}
                            />
                        </ButtonContainer>
                    </Form>
                </Modal>
            </div>
        );
    }
}
